import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import {
  fetchGstSlabs,
  createGstSlab,
  updateGstSlab,
  deleteGstSlab,
  GstSlab,
} from "../api/gstSlabsService";
import TableComponent from "../pages/TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import CustomAlert from "../view/common/alert/CustomAlert";
import Select from "react-select";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/gstslab.css";
import "../styles/common/custom-alert.css";
import "../styles/gstslab.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { updateFilters, resetFilters } from "../redux/slices/filterSlice";
import { getData } from "../api/ApiServices";
import {FormErrors} from "../models/gsttypes"

const GstSlabs: React.FC = () => {
  const [gstSlabs, setGstSlabs] = useState<GstSlab[]>([]);
  const [currentSlab, setCurrentSlab] = useState<GstSlab>({
    gstType: "",
    gstName: "",
    gstPercentage: 0,
    sgst: 0,
    cgst: 0,
    igst: 0,
    status: "ACTIVE",
  });
  const [originalSlab, setOriginalSlab] = useState<GstSlab | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [editMode, setEditMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("gstName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => {}
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => {}
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const  permission = useSelector((state:RootState) => state.permissions)
  const [initializeGstSlabs, isGstSlabaInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    isGstSlabaInitialized(true);
  }, [dispatch]);
  useEffect(() => {
    if (initializeGstSlabs) {
      loadGstSlabs();
    }
  }, [
    initializeGstSlabs,
    currentPage,
    searchTerm,
    itemsPerPage,
    filters,
    sortBy,
    sortOrder,
  ]);

  const loadGstSlabs = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData("/gstslabs", {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      console.log(response);

      if (response.data) {
        setGstSlabs(response.data.data || []);
        setTotalItems(response?.data?.pagination.total);
        setTotalPages(response.data.pagination.totalPages);
      } else if (response.error) {
        setError(response.error);
      }
    } catch (error) {
      console.error("Failed to fetch GST slabs:", error);
      setError("Failed to load GST slabs");
    }
    setIsLoading(false);
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => {},
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const handleAddSlab = () => {
    setCurrentSlab({
      gstType: "",
      gstName: "",
      gstPercentage: 0,
      sgst: 0,
      cgst: 0,
      igst: 0,
      status: "ACTIVE",
    });
    setOriginalSlab(null);
    setFormErrors({});
    setEditMode(false);
    setIsModalOpen(true);
  };

  const handleEdit = (slab: GstSlab) => {
    setCurrentSlab(slab);
    setOriginalSlab(JSON.parse(JSON.stringify(slab)));
    setFormErrors({});
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const slabToDelete = gstSlabs.find((slab) => slab._id === id);

    if (slabToDelete) {
      configureAlert(
        `Are you sure you want to InActive this GST slab: ${slabToDelete.gstName}?`,
        async () => {
          closeAlert();

          try {
            await deleteGstSlab(id);
            await loadGstSlabs();

            configureAlert(
              `GST slab has been InActive successfully.`,
              () => {
                closeAlert();
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          } catch (error) {
            console.error("Error deleting GST slab:", error);
            configureAlert(
              "Failed to delete GST slab.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    }
  };

 const validateForm = (): boolean => {
  const errors: FormErrors = {};
  let isValid = true;

  if (!currentSlab.gstType) {
    errors.gstType = "required";
    isValid = false;
  }

  if (!currentSlab.gstName.trim()) {
    errors.gstName = "required";
    isValid = false;
  }

  if (
    currentSlab.gstPercentage === null ||
    currentSlab.gstPercentage === undefined ||
    isNaN(currentSlab.gstPercentage)
  ) {
    errors.gstPercentage = "GST Percentage is required";
    isValid = false;
  } else if (currentSlab.gstPercentage < 0) {
    errors.gstPercentage = "GST Percentage cannot be negative";
    isValid = false;
  }

  if (currentSlab.gstType === "INTRA") {
    if (
      currentSlab.sgst === null ||
      currentSlab.sgst === undefined ||
      isNaN(Number(currentSlab.sgst))
    ) {
      errors.sgst = "SGST is required for INTRA GST type";
      isValid = false;
    }

    if (
      currentSlab.cgst === null ||
      currentSlab.cgst === undefined ||
      isNaN(Number(currentSlab.cgst))
    ) {
      errors.cgst = "CGST is required for INTRA GST type";
      isValid = false;
    }
    if (
      currentSlab.sgst !== null &&
      currentSlab.cgst !== null &&
      !isNaN(Number(currentSlab.sgst)) &&
      !isNaN(Number(currentSlab.cgst)) &&
      !isNaN(currentSlab.gstPercentage)
    ) {
      const sum = Number(currentSlab.sgst) + Number(currentSlab.cgst);
      if (sum !== currentSlab.gstPercentage) {
        errors.gstPercentage = `Sum of SGST (${currentSlab.sgst}%) and CGST (${currentSlab.cgst}%) must equal GST Percentage (${currentSlab.gstPercentage}%)`;
        isValid = false;
      }
    }
  }

  if (currentSlab.gstType === "INTER") {
    if (
      currentSlab.igst === null ||
      currentSlab.igst === undefined ||
      isNaN(Number(currentSlab.igst))
    ) {
      errors.igst = "IGST is required for INTER GST type";
      isValid = false;
    }
    if (
      currentSlab.igst !== null &&
      !isNaN(Number(currentSlab.igst)) &&
      !isNaN(currentSlab.gstPercentage)
    ) {
      if (Number(currentSlab.igst) !== currentSlab.gstPercentage) {
        errors.gstPercentage = `IGST (${currentSlab.igst}%) must equal GST Percentage (${currentSlab.gstPercentage}%)`;
        isValid = false;
      }
    }
  }

  if (!currentSlab.status) {
    errors.status = "Status is required";
    isValid = false;
  }

  setFormErrors(errors);
  return isValid;
};

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (editMode && originalSlab) {
      const hasChanges =
        JSON.stringify(currentSlab) !== JSON.stringify(originalSlab);

      if (!hasChanges) {
        setIsModalOpen(false);
        configureAlert(
          "No changes were made.",
          closeAlert,
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
        return;
      }
    }

    setIsLoading(true);
    try {
      let updatedSlab = { ...currentSlab };

      if (editMode) {
        const response = await updateGstSlab(
          currentSlab._id || "",
          updatedSlab
        );

        if (response.error) {
          throw new Error(response.error);
        }

        setIsModalOpen(false);

        configureAlert(
          `GST slab "${currentSlab.gstName}" has been updated successfully.`,
          () => {
            loadGstSlabs();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      } else {
        const response: any = await createGstSlab(updatedSlab);
        console.log(response);

        if (response?.status === 201) {
          configureAlert(
            `GST slab "${currentSlab.gstName}" has been added successfully.`,
            () => {
              loadGstSlabs();
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        } else {
          throw new Error("Unexpected response!");
        }
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error saving GST slab:", error);
      configureAlert(
        `Duplicate entry!`,
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      setIsModalOpen(false);
    }
    setIsLoading(false);
  };

  const handleCloseModal = () => {
    if (isModalOpen) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          closeAlert();
          setError(null);
          setFormErrors({});
          setOriginalSlab(null);
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else {
      setIsModalOpen(false);
      setError(null);
      setFormErrors({});
      setOriginalSlab(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setCurrentSlab((prev) => ({ ...prev, [name]: value }));
  };

  const columns = [
    { key: "gstType", label: "GST Type", sortable: true },
    { key: "gstName", label: "GST Name", sortable: true },
    {
      key: "gstPercentage",
      label: "GST Percentage",
      sortable: true,
      isRightAlign: true,
    },
    { key: "sgst", label: "SGST %", sortable: true, isRightAlign: true },
    { key: "cgst", label: "CGST %", sortable: true, isRightAlign: true },
    { key: "igst", label: "IGST %", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  const gstTypeOptions = [
    { value: "INTRA", label: "INTRA" },
    { value: "INTER", label: "INTER" },
  ];

  const statusOptions = [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const { name, value } = e.target;

  if (value === "" || (!isNaN(Number(value)) && Number(value) >= 0)) {
    const numValue = value === "" ? "" : Number(value);
    
    setCurrentSlab((prev) => {
      const updatedSlab = { ...prev, [name]: numValue };
      if (name === "sgst" || name === "cgst") {
        if (prev.gstType === "INTRA" && 
            updatedSlab.sgst !== null && 
            updatedSlab.cgst !== null && 
            !isNaN(Number(updatedSlab.sgst)) && 
            !isNaN(Number(updatedSlab.cgst))) {
          updatedSlab.gstPercentage = Number(updatedSlab.sgst) + Number(updatedSlab.cgst);
        }
      } else if (name === "igst") {
        if (prev.gstType === "INTER" && 
            updatedSlab.igst !== null && 
            !isNaN(Number(updatedSlab.igst))) {
          updatedSlab.gstPercentage = Number(updatedSlab.igst);
        }
      } else if (name === "gstPercentage") {
        if (prev.gstType === "INTRA" && numValue !== "") {
          const halfValue = Number(numValue) / 2;
          updatedSlab.sgst = halfValue;
          updatedSlab.cgst = halfValue;
        } else if (prev.gstType === "INTER" && numValue !== "") {
          updatedSlab.igst = Number(numValue);
        }
      }
      
      return updatedSlab;
    });

    if (name === 'sgst' || name === 'cgst' || name === 'igst') {
      setFormErrors((prev) => ({ ...prev, [name]: undefined, gstPercentage: undefined }));
    }
  }
};

  const handleGstTypeChange = (
  selectedOption: { value: string; label: string } | null
) => {
  if (!selectedOption) return;
  const gstType = selectedOption.value;

  setCurrentSlab((prev) => {
    const updatedSlab = {
      ...prev,
      gstType,
    };
    
    if (gstType === "INTRA") {
      updatedSlab.igst = null;
      
      if (prev.gstPercentage && !isNaN(prev.gstPercentage)) {
        const halfValue = prev.gstPercentage / 2;
        updatedSlab.sgst = halfValue;
        updatedSlab.cgst = halfValue;
      } else {
        updatedSlab.sgst = 0;
        updatedSlab.cgst = 0;
      }
    } else { 
      updatedSlab.sgst = null;
      updatedSlab.cgst = null;
      
      if (prev.gstPercentage && !isNaN(prev.gstPercentage)) {
        updatedSlab.igst = prev.gstPercentage;
      } else {
        updatedSlab.igst = 0;
      }
    }
    
    return updatedSlab;
  });

  setFormErrors((prev) => ({
    ...prev,
    gstType: undefined,
    sgst: undefined,
    cgst: undefined,
    igst: undefined,
    gstPercentage: undefined,
  }));
};
  const handleStatusChange = (
    selectedOption: { value: string; label: string } | null
  ) => {
    if (!selectedOption) return;
    setCurrentSlab((prev) => ({
      ...prev,
      status: selectedOption.value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      status: undefined,
    }));
  };

  const preventScroll = (e: WheelEvent) => {
    e.preventDefault();
  };

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>General</span>
              <span>GST Slabs</span>
            </div>
            {permission.menus.gstslabs.create ?
            <div className="items-button-container">
              <button
                className="item-models-add-btn tooltip-wrapper"
                onClick={handleAddSlab}
                disabled={isLoading}
              >
                <Plus size={16} />
              </button>
            </div>
            :<></>}
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="spinner"></div>
      ) : (
        <div className="box-container">
          <TableComponent
            columns={columns}
            data={gstSlabs}
            onEdit={handleEdit}
            onDelete={handleDelete}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
          />

          {totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              totalItems={totalItems}
            />
          )}
        </div>
      )}

      {isModalOpen && (
        <div className="item-models-modal-overlay">
          <div className="item-models-modal">
            <div className="item-models-modal-header">
              <h5>{editMode ? "Edit GST Slab" : "Add New GST Slab"}</h5>
              <button
                className="item-models-modal-close"
                onClick={handleCloseModal}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>
                  GST Type :<span className="required">*</span>{" "}
                </label>
                <Select
                  name="gstType"
                  options={gstTypeOptions}
                  value={gstTypeOptions.find(
                    (option) => option.value === currentSlab.gstType
                  )}
                  onChange={handleGstTypeChange}
                  placeholder="GST Type"
                  className={`react-select-container ${
                    formErrors.gstType ? "error-input" : ""
                  }`}
                  classNamePrefix="react-select"
                />
                {formErrors.gstType && (
                  <div className="error-text">{formErrors.gstType}</div>
                )}
              </div>

              <div className="form-group">
                <label>
                  GST Name :<span className="required">*</span>{" "}
                </label>
                <input
                  type="text"
                  name="gstName"
                  placeholder="Enter GST Model"
                  value={currentSlab.gstName}
                  onChange={(e) => {
                    setCurrentSlab({ ...currentSlab, gstName: e.target.value });
                    if (e.target.value.trim()) {
                      setFormErrors((prev) => ({
                        ...prev,
                        gstName: undefined,
                      }));
                    }
                  }}
                  className={formErrors.gstName ? "error-input" : ""}
                />
                {formErrors.gstName && (
                  <div className="error-text">{formErrors.gstName}</div>
                )}
              </div>

              

              <div className="form-group">
                <label>SGST (%) : </label>
                <input
                  type="number"
                  name="sgst"
                  placeholder={currentSlab.gstType === "INTER" ? "N/A" : "eg:5"}
                  value={currentSlab.sgst ?? ""}
                  onFocus={(e) =>
                    e.target.addEventListener("wheel", preventScroll, {
                      passive: false,
                    })
                  }
                  onBlur={(e) =>
                    e.target.removeEventListener("wheel", preventScroll)
                  }
                  onChange={(e) => {
                    handleNumericChange(e);
                    if (
                      currentSlab.gstType === "INTRA" &&
                      e.target.value !== "" &&
                      !isNaN(Number(e.target.value))
                    ) {
                      setFormErrors((prev) => ({ ...prev, sgst: undefined }));
                    }
                  }}
                  disabled={currentSlab.gstType === "INTER"}
                  className={formErrors.sgst ? "error-input" : ""}
                />
                {formErrors.sgst && (
                  <div className="error-text">{formErrors.sgst}</div>
                )}
              </div>

              <div className="form-group">
                <label>CGST (%) : </label>
                <input
                  type="number"
                  name="cgst"
                  placeholder={currentSlab.gstType === "INTER" ? "N/A" : "eg:5"}
                  value={currentSlab.cgst ?? ""}
                  onFocus={(e) =>
                    e.target.addEventListener("wheel", preventScroll, {
                      passive: false,
                    })
                  }
                  onBlur={(e) =>
                    e.target.removeEventListener("wheel", preventScroll)
                  }
                  onChange={(e) => {
                    handleNumericChange(e);
                    if (
                      currentSlab.gstType === "INTRA" &&
                      e.target.value !== "" &&
                      !isNaN(Number(e.target.value))
                    ) {
                      setFormErrors((prev) => ({ ...prev, cgst: undefined }));
                    }
                  }}
                  disabled={currentSlab.gstType === "INTER"}
                  className={formErrors.cgst ? "error-input" : ""}
                />
                {formErrors.cgst && (
                  <div className="error-text">{formErrors.cgst}</div>
                )}
              </div>

              <div className="form-group">
                <label>IGST (%) : </label>
                <input
                  type="number"
                  name="igst"
                  placeholder={currentSlab.gstType === "INTRA" ? "N/A" : "eg:5"}
                  value={currentSlab.igst ?? ""}
                  onFocus={(e) =>
                    e.target.addEventListener("wheel", preventScroll, {
                      passive: false,
                    })
                  }
                  onBlur={(e) =>
                    e.target.removeEventListener("wheel", preventScroll)
                  }
                  onChange={(e) => {
                    handleNumericChange(e);
                    if (
                      currentSlab.gstType === "INTER" &&
                      e.target.value !== "" &&
                      !isNaN(Number(e.target.value))
                    ) {
                      setFormErrors((prev) => ({ ...prev, igst: undefined }));
                    }
                  }}
                  disabled={currentSlab.gstType === "INTRA"}
                  className={formErrors.igst ? "error-input" : ""}
                />
                {formErrors.igst && (
                  <div className="error-text">{formErrors.igst}</div>
                )}
              </div>
              <div className="form-group">
                <label>
                  GST Percent (%) :<span className="required">*</span>{" "}
                </label>
                <input
                  type="number"
                  name="gstPercentage"
                  placeholder="Enter GST Slab %"
                  value={currentSlab.gstPercentage ?? ""}
                  onFocus={(e) =>
                    e.target.addEventListener("wheel", preventScroll, {
                      passive: false,
                    })
                  }
                  onBlur={(e) =>
                    e.target.removeEventListener("wheel", preventScroll)
                  }
                  onChange={(e) => {
                    handleNumericChange(e);
                    if (
                      e.target.value !== "" &&
                      !isNaN(Number(e.target.value)) &&
                      Number(e.target.value) >= 0
                    ) {
                      setFormErrors((prev) => ({
                        ...prev,
                        gstPercentage: undefined,
                      }));
                    }
                  }}
                  className={formErrors.gstPercentage ? "error-input" : ""}
                />
                {formErrors.gstPercentage && (
                  <div className="error-text">{formErrors.gstPercentage}</div>
                )}
              </div>

              {editMode && (
                <div className="form-group">
                  <label>Status : </label>
                  <Select
                    name="status"
                    options={statusOptions}
                    value={statusOptions.find(
                      (option) => option.value === currentSlab.status
                    )}
                    onChange={handleStatusChange}
                    placeholder="Select Status"
                    className={`react-select-container ${
                      formErrors.status ? "error-input" : ""
                    }`}
                    classNamePrefix="react-select"
                  />
                  {formErrors.status && (
                    <div className="error-text">{formErrors.status}</div>
                  )}
                </div>
              )}

              <button type="submit">{editMode ? "Update" : "Create"}</button>
            </form>
          </div>
        </div>
      )}

      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default GstSlabs;
